import { UiSettingsIdentifier } from '../../../../services';
import { ProductionInEuType, RequestedType, SubmissionEmptyStatus, SubmissionStatus, SubmissionStatusWithEmpty, translationKeys } from '../../../../core';
import { typeField } from '@avano/avano-components/utils';
import { OrganizationDto, OrganizationGroupDto, ProductSearchResultDto } from '@scip/api';
import { TableColumnConfiguration, TableColumnFilterMode } from '@avano/avano-components/avano-table';
import { SelectItem } from 'primeng/api';

export const tableConfiguration = {
  tableSettings: {
    exportEnabled: true,
    settingsEnabled: true,
    settingsKeepFilters: true,
    settingsKeepRows: true,
    refreshEnabled: true,
    settingsDbIdentifier: UiSettingsIdentifier.ProductsTable
  },
  withPagination: true,
  useTableDefaults: true
};

// We filter the submissions always to only these ones and only these are valid for the user-filter too
export const filterStateForSubmissions: SubmissionStatusWithEmpty[] = [
  SubmissionStatus.DOSSIER_REQUESTED,
  SubmissionStatus.DOSSIER_FILE_IN_PREPARATION,
  SubmissionStatus.DOSSIER_FAILED_TO_CREATE,
  SubmissionStatus.DOSSIER_FILE_CREATED,
  SubmissionStatus.DOSSIER_SENT_TO_ECHA,
  SubmissionStatus.PENDING,
  SubmissionStatus.SUBMITTED,
  SubmissionStatus.ALL_PARTS_COMPLETED,
  SubmissionStatus.ONE_OR_MORE_FAILED,
  SubmissionStatus.VALIDATION_SUCCEEDED,
  SubmissionStatus.FAILED,
  SubmissionStatus.VALIDATION_FAILED,
  SubmissionStatus.DOSSIER_TOO_LARGE,
  SubmissionEmptyStatus.SUBMISSION_EMPTY
];

export const filterStateForProductionInEU: ProductionInEuType[] = [
  ProductionInEuType.EU_IMPORTED,
  ProductionInEuType.EU_PRODUCED,
  ProductionInEuType.EU_PRODUCED_AND_IMPORTED,
  ProductionInEuType.NO_DATA
];

export const columnKeys = {
  organizationName: typeField<ProductSearchResultDto>('organizationName'),
  scipDossierSubmissionStatus: typeField<ProductSearchResultDto>('scipDossierSubmissionStatus'),
  requestedType: typeField<ProductSearchResultDto>('requestedType'),
  name: typeField<ProductSearchResultDto>('name'),
  number: typeField<ProductSearchResultDto>('number'),
  articleCategories: typeField<ProductSearchResultDto>('articleCategories'),
  productionInEU: typeField<ProductSearchResultDto>('productionInEU'),
  updatedAt: typeField<ProductSearchResultDto>('updatedAt'),
  scipRelevantChanged: typeField<ProductSearchResultDto>('scipRelevantChanged'),
  euMarketRelevantChanged: typeField<ProductSearchResultDto>('euMarketRelevantChanged'),
  otherArticleIdentifiers: typeField<ProductSearchResultDto>('otherArticleIdentifiers'),
  orgGroupName: typeField<ProductSearchResultDto>('orgGroupName'),
  lockedForUpdate: typeField<ProductSearchResultDto>('lockedForUpdate'),
  downloadError: typeField<ProductSearchResultDto>('downloadError'),
  errorMessage: typeField<ProductSearchResultDto>('errorMessage'),
  echaResponseStatus: typeField<ProductSearchResultDto>('echaResponseStatus'),
  submissionDate: typeField<ProductSearchResultDto>('submissionDate'),
  info: 'info', // Is not a field of the dto itself, but it is a column
  orgVerificationstatus: typeField<ProductSearchResultDto>('verified'),
  validationErrors: typeField<ProductSearchResultDto>('validationErrors')
};

export const getTableColumns = (
  filterableSubmissionStatusValues: SelectItem[],
  filterableProductionInEUValues: SelectItem[],
  organizations: OrganizationDto[],
  organizationGroups: OrganizationGroupDto[] = undefined
): TableColumnConfiguration[] => {
  // We need a reference to that column because we change it asynchronously
  const organizationsColumn = {
    field: columnKeys.organizationName,
    show: true,
    minWidth: '130px',
    sortable: true,
    filterable: true,
    filterConfiguration: {
      filterMode: TableColumnFilterMode.AUTOCOMPLETE_SINGLE,
      values: [],
      valuesLabel: typeField<OrganizationDto>('name')
    },
    sortField: 'assignedOrganizationName',
    header: translationKeys.productsTableComponent.typescript.organizationName
  };

  // Set our organizations into the column filter
  if (organizations?.length) {
    organizationsColumn.filterConfiguration.values = organizations;
  } else {
    // We have no organizations? (error or really empty) So what should the user filter for?
    // Let's disable then the filter, should be better
    organizationsColumn.filterable = false;
  }

  const submissionStatusColumn = {
    field: columnKeys.scipDossierSubmissionStatus,
    show: true,
    minWidth: '130px',
    filterable: true,
    sortable: false,
    filterConfiguration: {
      filterMode: TableColumnFilterMode.AUTOCOMPLETE_MULTIPLE,
      values: [...filterableSubmissionStatusValues]
    },
    header: translationKeys.productsTableComponent.typescript.submissionStatusHeadertext
  };

  const allColumns: TableColumnConfiguration[] = [
    {
      field: columnKeys.requestedType,
      show: true,
      minWidth: '75px',
      sortable: true,
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.AUTOCOMPLETE_SINGLE,
        values: RequestedType
      },
      sortField: 'assignedRequestedType',
      header: translationKeys.productsTableComponent.typescript.requestedTypeHeaderText
    },
    {
      field: columnKeys.name,
      show: true,
      minWidth: '130px',
      sortable: true,
      filterable: true,
      header: translationKeys.productsTableComponent.typescript.productNameHeaderText
    },
    {
      field: columnKeys.number,
      show: true,
      minWidth: '130px',
      sortable: true,
      filterable: true,
      header: translationKeys.productsTableComponent.typescript.productNumberHeaderText
    },
    {
      field: columnKeys.articleCategories,
      show: true,
      sortable: false,
      filterable: false,
      minWidth: '130px',
      header: translationKeys.productsTableComponent.typescript.articleCategoryHeadertext
    },
    {
      field: columnKeys.productionInEU,
      show: true,
      minWidth: '130px',
      sortable: true,
      sortField: 'euProductionType',
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.AUTOCOMPLETE_SINGLE,
        values: [...filterableProductionInEUValues],
        isSingleSelectLabelField: true
      },
      header: translationKeys.productsTableComponent.typescript.productionInEUHeaderText
    },
    submissionStatusColumn,
    organizationsColumn,
    {
      field: columnKeys.orgVerificationstatus,
      show: true,
      minWidth: '150px',
      sortable: false,
      filterable: false,
      excludeExcel: true,
      header: translationKeys.productsTableComponent.typescript.orgVerificationstatus
    },
    {
      field: columnKeys.updatedAt,
      show: true,
      minWidth: '90px',
      sortable: true,
      sortField: 'lastModifiedDate',
      header: translationKeys.productsTableComponent.typescript.lastUpdated,
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.DATEPICKER_SINGLE
      }
    },
    {
      field: columnKeys.submissionDate,
      show: true,
      minWidth: '90px',
      sortable: true,
      sortField: columnKeys.submissionDate,
      header: translationKeys.productsTableComponent.typescript.lastSubmissionDate,
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.DATEPICKER_SINGLE
      }
    },
    {
      field: columnKeys.scipRelevantChanged,
      show: true,
      minWidth: '90px',
      sortable: true,
      sortField: 'scipRelevantChanged',
      header: translationKeys.productsTableComponent.typescript.scipRelevantChanged,
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.DATEPICKER_SINGLE
      }
    },
    {
      field: columnKeys.euMarketRelevantChanged,
      show: true,
      minWidth: '90px',
      sortable: true,
      sortField: 'euMarketRelevantChanged',
      header: translationKeys.productsTableComponent.typescript.euMarketRelevantChanged,
      filterable: true,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.DATEPICKER_SINGLE
      }
    },
    {
      field: columnKeys.info,
      show: true,
      minWidth: '35px',
      sortable: false,
      filterable: false,
      excludeExcel: true,
      header: translationKeys.productsTableComponent.typescript.info
    },
    {
      field: columnKeys.otherArticleIdentifiers,
      show: false,
      minWidth: '130px',
      sortable: false,
      filterable: false,
      header: translationKeys.productsTableComponent.typescript.otherArticleIdentifiers
    }
  ];

  if (organizationGroups) {
    const organizationGroupColumn = {
      field: columnKeys.orgGroupName,
      show: false,
      minWidth: '130px',
      sortable: false,
      header: translationKeys.productsTableComponent.typescript.organizationGroup,
      filterable: false,
      filterConfiguration: {
        filterMode: TableColumnFilterMode.AUTOCOMPLETE_SINGLE,
        values: []
      }
    };

    if (organizationGroups?.length) {
      organizationGroupColumn.filterConfiguration.values = organizationGroups.map((singleGroup: OrganizationGroupDto) => {
        return { label: singleGroup.name, value: singleGroup.name };
      });
      organizationGroupColumn.filterable = true;
    }
    allColumns.push(organizationGroupColumn);
  }

  return allColumns;
};
